/**
 * Production verification shell icons.
 *
 * Extracted from excellent/client/prototypes/verification/ui.tsx for WP1a
 * (#46597). Kept local to the verification chrome so product mode does not
 * import the prototype kit.
 */

import { type ReactNode } from "react";

/* ══════════════════════════════════════════════════════════════════════════
   ICONS — 16px stroke set, currentColor
   ══════════════════════════════════════════════════════════════════════════ */

function Svg({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function CaretSvg({ children, size = 8 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10 10"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export type IconName =
  | "gauge"
  | "inbox"
  | "bell"
  | "flag"
  | "play"
  | "plug"
  | "shield"
  | "file"
  | "folder"
  | "book"
  | "seal"
  | "package"
  | "door"
  | "cpu"
  | "chart"
  | "server"
  | "code"
  | "search"
  | "chevron-down"
  | "chevron-right"
  | "chevron-left"
  | "caret-down"
  | "caret-right"
  | "external"
  | "copy"
  | "check"
  | "x"
  | "alert"
  | "info"
  | "clock"
  | "download"
  | "refresh"
  | "plus"
  | "link"
  | "eye"
  | "lock"
  | "menu"
  | "arrow-right"
  | "users"
  | "user"
  | "logout";

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  switch (name) {
    case "gauge":
      return (
        <Svg size={size}>
          <path d="M12 14a2 2 0 100-4 2 2 0 000 4zM13.4 10.6L18 6M3.5 19a9 9 0 1117 0" />
        </Svg>
      );
    case "inbox":
      return (
        <Svg size={size}>
          <path d="M4 13l2-8h12l2 8v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM4 13h4l1 2h6l1-2h4" />
        </Svg>
      );
    case "bell":
      return (
        <Svg size={size}>
          <path d="M18 8a6 6 0 00-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9zM10 21h4" />
        </Svg>
      );
    case "flag":
      return (
        <Svg size={size}>
          <path d="M5 21V4M5 4h11l-1.5 4L16 12H5" />
        </Svg>
      );
    case "play":
      return (
        <Svg size={size}>
          <path d="M8 5.5v13l10-6.5-10-6.5z" />
        </Svg>
      );
    case "plug":
      return (
        <Svg size={size}>
          <path d="M9 3v6M15 3v6M6 9h12v2a6 6 0 01-6 6 6 6 0 01-6-6V9zM12 17v4" />
        </Svg>
      );
    case "shield":
      return (
        <Svg size={size}>
          <path d="M12 3l7 3v5.5c0 4.3-2.9 7.9-7 9.5-4.1-1.6-7-5.2-7-9.5V6l7-3z" />
        </Svg>
      );
    case "file":
      return (
        <Svg size={size}>
          <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7l-4-4zM14 3v4h4M9 13h6M9 17h4" />
        </Svg>
      );
    case "folder":
      return (
        <Svg size={size}>
          <path d="M3 6a2 2 0 012-2h5l2 3h7a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
        </Svg>
      );
    case "book":
      return (
        <Svg size={size}>
          <path d="M4 5a2 2 0 012-2h12v18H6a2 2 0 01-2-2V5zM8 3v18" />
        </Svg>
      );
    case "seal":
      return (
        <Svg size={size}>
          <path d="M12 3l2.2 1.6 2.7-.3 1 2.5 2.3 1.4-.7 2.6.7 2.6-2.3 1.4-1 2.5-2.7-.3L12 19l-2.2-1.6-2.7.3-1-2.5L3.8 13.8l.7-2.6-.7-2.6 2.3-1.4 1-2.5 2.7.3L12 3zM9.5 11.8l1.9 1.9 3.4-3.4" />
        </Svg>
      );
    case "package":
      return (
        <Svg size={size}>
          <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2L12 3zM4 7.2l8 4.2 8-4.2M12 11.4V21" />
        </Svg>
      );
    case "door":
      return (
        <Svg size={size}>
          <path d="M5 21V4a1 1 0 011-1h9a1 1 0 011 1v17M5 21h14M13 12h.01" />
        </Svg>
      );
    case "cpu":
      return (
        <Svg size={size}>
          <path d="M8 8h8v8H8V8zM5 5h14v14H5V5zM10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" />
        </Svg>
      );
    case "chart":
      return (
        <Svg size={size}>
          <path d="M4 20V4M4 20h16M8 20v-6M12.5 20V8M17 20v-9" />
        </Svg>
      );
    case "server":
      return (
        <Svg size={size}>
          <path d="M4 4h16v6H4V4zM4 14h16v6H4v-6zM7.5 7h.01M7.5 17h.01" />
        </Svg>
      );
    case "code":
      return (
        <Svg size={size}>
          <path d="M9 6l-5 6 5 6M15 6l5 6-5 6" />
        </Svg>
      );
    case "search":
      return (
        <Svg size={size}>
          <path d="M11 18a7 7 0 100-14 7 7 0 000 14zM16.2 16.2L21 21" />
        </Svg>
      );
    case "chevron-down":
      return (
        <Svg size={size}>
          <path d="M6 9l6 6 6-6" />
        </Svg>
      );
    case "chevron-right":
      return (
        <Svg size={size}>
          <path d="M9 6l6 6-6 6" />
        </Svg>
      );
    case "chevron-left":
      return (
        <Svg size={size}>
          <path d="M15 6l-6 6 6 6" />
        </Svg>
      );
    case "caret-down":
      return (
        <CaretSvg size={size}>
          <path d="M1.6 3.2L5 7.4 8.4 3.2z" />
        </CaretSvg>
      );
    case "caret-right":
      return (
        <CaretSvg size={size}>
          <path d="M3.2 1.6L7.4 5 3.2 8.4z" />
        </CaretSvg>
      );
    case "external":
      return (
        <Svg size={size}>
          <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
        </Svg>
      );
    case "copy":
      return (
        <Svg size={size}>
          <path d="M9 9h10v10H9V9zM15 9V5H5v10h4" />
        </Svg>
      );
    case "check":
      return (
        <Svg size={size}>
          <path d="M4 12.5l5.5 5.5L20 7" />
        </Svg>
      );
    case "x":
      return (
        <Svg size={size}>
          <path d="M6 6l12 12M18 6L6 18" />
        </Svg>
      );
    case "alert":
      return (
        <Svg size={size}>
          <path d="M12 4l9 16H3l9-16zM12 10v4M12 17h.01" />
        </Svg>
      );
    case "info":
      return (
        <Svg size={size}>
          <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 8h.01" />
        </Svg>
      );
    case "clock":
      return (
        <Svg size={size}>
          <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 7.5V12l3 2" />
        </Svg>
      );
    case "download":
      return (
        <Svg size={size}>
          <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
        </Svg>
      );
    case "refresh":
      return (
        <Svg size={size}>
          <path d="M20 12a8 8 0 11-2.6-5.9M20 4v4h-4" />
        </Svg>
      );
    case "plus":
      return (
        <Svg size={size}>
          <path d="M12 5v14M5 12h14" />
        </Svg>
      );
    case "link":
      return (
        <Svg size={size}>
          <path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 10-5.7-5.7l-1 1M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 105.7 5.7l1-1" />
        </Svg>
      );
    case "eye":
      return (
        <Svg size={size}>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12S18 18.5 12 18.5 2.5 12 2.5 12zM12 14.8a2.8 2.8 0 100-5.6 2.8 2.8 0 000 5.6z" />
        </Svg>
      );
    case "lock":
      return (
        <Svg size={size}>
          <path d="M6 11h12v9H6v-9zM8.5 11V7.5a3.5 3.5 0 017 0V11" />
        </Svg>
      );
    case "menu":
      return (
        <Svg size={size}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </Svg>
      );
    case "arrow-right":
      return (
        <Svg size={size}>
          <path d="M4 12h15M13 6l6 6-6 6" />
        </Svg>
      );
    case "users":
      return (
        <Svg size={size}>
          <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M20 8a4 4 0 010 8M22 21v-2a4 4 0 00-3-3.87M8 7a4 4 0 108 0 4 4 0 00-8 0" />
        </Svg>
      );
    case "user":
      return (
        <Svg size={size}>
          <path d="M20 21a8 8 0 00-16 0M12 11a4 4 0 100-8 4 4 0 000 8z" />
        </Svg>
      );
    case "logout":
      return (
        <Svg size={size}>
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
        </Svg>
      );
  }
}
